// CRM-owned members. Org-level access list — who can enter the CRM and what
// they can do. Backed by /api/v1/crm/members. Add flow picks from TG users in
// the same org who are not yet CRM members (eligible-users endpoint).

import React, { useState, useMemo } from "react";
import { Loader2, Search, Users, Plus, Trash2 } from "lucide-react";
import { Select as AntSelect } from "antd";
import toast from "react-hot-toast";
import { Button, Badge } from "../../atoms";
import {
  useTeamMembers,
  formatMember,
  useEligibleUsers,
  useAddCrmMember,
  useUpdateCrmMember,
  useRemoveCrmMember,
} from "../../hooks/useTeamMembers";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";

// We surface only two roles in the UI: Owner (full admin) and User (everyone
// else). Backend still has 4 (owner/manager/agent/viewer) for legacy reasons,
// but we don't expose manager/viewer in the dropdown — picking "User" writes
// `agent` server-side, and existing manager/viewer rows show as "User" too.
// This is intentionally a UI-only simplification; the backend role enum is
// untouched so legacy data keeps working.
const SIMPLE_ROLES = [
  { value: "owner", label: "Owner" },
  { value: "agent", label: "User" }, // "User" maps to backend 'agent'
];

// Normalize any backend role into one of the two UI values for display +
// dropdown selection. Anything that isn't 'owner' collapses to 'agent'.
function uiRole(role) {
  return role === "owner" ? "owner" : "agent";
}

function roleTone(role) {
  return role === "owner" ? "primary" : "neutral";
}

function roleLabel(role) {
  return uiRole(role) === "owner" ? "Owner" : "User";
}

function AddMemberPanel({ onClose }) {
  const { data: eligible = [], isLoading } = useEligibleUsers();
  const addMut = useAddCrmMember();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("agent");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return eligible;
    return eligible.filter((u) =>
      [u.name, u.email].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [eligible, q]);

  const handleAdd = async (userId) => {
    try {
      await addMut.mutateAsync({ userId, role });
      toast.success("Member added");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <div style={{ marginBottom: 14, border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 12, background: "var(--color-bg-base)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>Add CRM member</strong>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Role</label>
        <AntSelect
          value={role}
          onChange={(v) => setRole(v)}
          options={SIMPLE_ROLES}
          style={{ width: 130, height: 32 }}
        />
        <Button variant="secondary" size="sm" onClick={onClose}>Done</Button>
      </div>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search TG users in your org"
          style={inputStyle({ width: "100%", paddingLeft: 30, height: 32, fontSize: 12 })}
        />
      </div>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, color: "var(--color-text-secondary)", fontSize: 12 }}>
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: 8 }}>
            {q ? "No matches." : "Everyone in this org is already a CRM member."}
          </div>
        )}
        {filtered.map((u) => (
          <button
            key={u._id}
            type="button"
            onClick={() => handleAdd(u._id)}
            disabled={addMut.isPending}
            style={pickerRowStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-surface)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name || u.email}</div>
              {u.email && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{u.email}</div>}
            </span>
            <Plus size={13} style={{ color: "var(--color-text-secondary)" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MembersSection() {
  const { members, isLoading, isError, error } = useTeamMembers();
  const updateMut = useUpdateCrmMember();
  const removeMut = useRemoveCrmMember();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmMember, setConfirmMember] = useState(null);

  const filtered = useMemo(() => {
    if (!q.trim()) return members;
    const needle = q.toLowerCase();
    return members.filter((u) =>
      [u.firstName, u.lastName, u.email].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [members, q]);

  const handleRoleChange = async (m, role) => {
    try {
      await updateMut.mutateAsync({ membershipId: m.membershipId, patch: { role } });
      toast.success("Role updated");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleRemove = (m) => setConfirmMember(m);

  const performRemove = async () => {
    if (!confirmMember) return;
    try {
      await removeMut.mutateAsync(confirmMember.membershipId);
      setConfirmMember(null);
      toast.success("Member removed");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={15} style={{ color: "var(--crm-primary)" }} />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>CRM members</h2>
            <Badge tone="neutral">{members.length}</Badge>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
            Anyone listed here can sign into the CRM. Add TG users from your org and assign roles.
          </p>
        </div>
        {!adding && (
          <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => setAdding(true)}>
            Add member
          </Button>
        )}
      </header>

      {adding && <AddMemberPanel onClose={() => setAdding(false)} />}

      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search CRM members"
          style={inputStyle({ width: "100%", paddingLeft: 32, height: 34 })}
        />
      </div>

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16, color: "var(--color-text-secondary)" }}>
          <Loader2 size={14} className="animate-spin" /> Loading members…
        </div>
      )}
      {isError && (
        <div style={{ padding: 12, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", borderRadius: 8, fontSize: 13 }}>
          Couldn't load members: {error?.response?.data?.error || error?.message || "unknown error"}
        </div>
      )}
      {!isLoading && !isError && filtered.length === 0 && (
        <div style={{ padding: 12, fontSize: 13, color: "var(--color-text-secondary)" }}>
          {q ? "No members match your search." : "No CRM members yet — add one to get started."}
        </div>
      )}
      {!isLoading && !isError && filtered.length > 0 && (
        <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
          {filtered.map((u, i) => (
            <div
              key={u._id}
              style={{
                display: "grid", gridTemplateColumns: "36px 1fr auto auto", gap: 12,
                alignItems: "center", padding: "10px 14px",
                borderBottom: i < filtered.length - 1 ? "1px solid var(--color-border-subtle)" : "none",
                background: i % 2 === 0 ? "transparent" : "var(--color-bg-base)",
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--crm-primary-soft)", color: "var(--crm-primary)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 12 }}>
                {u.avatarUrl
                  ? <img src={u.avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                  : [u.firstName, u.lastName].filter(Boolean).map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "?"}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {formatMember(u)}
                </div>
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u.email}
                </div>
              </div>
              <AntSelect
                value={uiRole(u.role)}
                onChange={(v) => handleRoleChange(u, v)}
                disabled={updateMut.isPending}
                options={SIMPLE_ROLES}
                style={{ width: 130, height: 32 }}
              />
              <button
                type="button"
                onClick={() => handleRemove(u)}
                title="Remove from CRM"
                style={iconBtnStyle}
                disabled={removeMut.isPending}
              >
                <Trash2 size={13} />
              </button>
              {/* role badge fallback for narrow viewports — hidden behind the select on wide ones */}
              <span style={{ display: "none" }}><Badge tone={roleTone(u.role)}>{roleLabel(u.role)}</Badge></span>
            </div>
          ))}
        </div>
      )}
      <ConfirmDeleteModal
        open={!!confirmMember}
        onClose={() => setConfirmMember(null)}
        onConfirm={performRemove}
        title="Remove member?"
        message={confirmMember ? `${formatMember(confirmMember)} will lose access to the CRM. They keep their TG account; only CRM access is revoked.` : ""}
        confirmLabel="Remove member"
        isPending={removeMut.isPending}
      />
    </section>
  );
}

function inputStyle(extra = {}) {
  return {
    height: 34, padding: "0 12px",
    borderRadius: 8, border: "1px solid var(--color-border-subtle)",
    background: "var(--color-bg-base)", color: "var(--color-text-primary)",
    fontSize: 13, outline: "none",
    ...extra,
  };
}

const selectStyle = {
  height: 30, padding: "0 8px",
  borderRadius: 6, border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-base)", color: "var(--color-text-primary)",
  fontSize: 12, outline: "none", cursor: "pointer",
};

const pickerRowStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  width: "100%", padding: "8px 10px", border: "none", background: "transparent",
  cursor: "pointer", textAlign: "left", borderRadius: 6,
};

const iconBtnStyle = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 28, height: 28, border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-base)", borderRadius: 6, cursor: "pointer",
  color: "var(--crm-danger)",
};

